import { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Step, STEP_KEYS } from "./steps";

export const stepFromSearch = (search: string): Step | null => {
  const i = STEP_KEYS.indexOf(new URLSearchParams(search).get("step") as (typeof STEP_KEYS)[number]);
  return i < 0 ? null : ((i + 1) as Step);
};

export interface BookingFlow {
  open: boolean;
  /** `replace`: the address is corrected, not a place the renter went to, so Back does not return to it. */
  setOpen: (open: boolean, replace?: boolean) => void;
  step: Step;
  setStep: (step: Step, replace?: boolean) => void;
}

/**
 * Where the booking steps are open and which step is showing: in the address, on phones and desktop alike
 * (1NOW-11173). `?step=` sits on the car page's own route next to `selectedCarId`, so the steps are history
 * entries, Back and Forward move between them, and a pageview-based tracker sees the same URLs whichever
 * layout is on screen.
 */
export const useBookingFlow = (): BookingFlow => {
  const { search } = useLocation();
  const navigate = useNavigate();
  // The address as the last call left it: two calls in one event (setStep, then setOpen) must make one entry.
  const latest = useRef(search);
  latest.current = search;

  const go = (step: Step | null, replace = false) => {
    const q = new URLSearchParams(latest.current);
    if (step === null) q.delete("step");
    else q.set("step", STEP_KEYS[step - 1]);
    if (q.toString() === new URLSearchParams(latest.current).toString()) return;
    latest.current = q.toString() ? `?${q}` : "";
    navigate({ search: latest.current }, { replace });
  };

  const urlStep = stepFromSearch(search);
  return {
    open: urlStep !== null,
    setOpen: (o, replace) => {
      if (!o) go(null, replace);
      // Opened with a step already set in this event (the review, say): that one stands.
      else if (stepFromSearch(latest.current) === null) go(1, replace);
    },
    step: urlStep ?? 1,
    setStep: go,
  };
};
